import { usePopover } from '@/hooks/usePopover';
import { useFileDownload } from '@/hooks/useFileDownload';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { useTenants } from '@/services/tenants/useTenants';
import { DownloadFormat } from '@/utils/downloadUtils';
import { FileDownloadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { CircularProgress, Divider, IconButton, ListItemIcon, Tooltip, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { StyledList, StyledListHeader, StyledListItem } from '../AssetsPage/PopoverList';
import { BodyText } from '../BodyText';
import { RoundedPopover } from '../Popover/RoundedPopover';

export const ReportDownload = ({ id }: { id: string }) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const theme = useTheme();

  const {
    open: openDownload,
    close: closeDownload,
    isOpen: isDownloadOpen,
    anchorElement: downloadAnchorElement,
  } = usePopover();

  const { download, isDownloading } = useFileDownload();

  const handleDownload = useCallback(
    async (format: DownloadFormat) => {
      if (!id || !tenantId) return;

      closeDownload();
      await download(
        `/tenant/${tenantId}/reports/${id}/file?format=${format}&baseUrl=${encodeURIComponent(globalThis.location.origin)}`,
        {
          filename: `report-${id}.${format}`,
          errorMessage: `Failed to download ${format.toUpperCase()}`,
        }
      );
    },
    [id, tenantId, closeDownload, download]
  );

  const formats: DownloadFormat[] = ['csv', 'xlsx'];
  const formatLabels: Record<DownloadFormat, string> = {
    csv: 'CSV',
    xlsx: 'XLSX',
    xls: 'XLS',
  };

  return (
    <>
      <Tooltip title='Download Report' placement='bottom'>
        <span>
          <IconButton
            size='small'
            onClick={openDownload}
            disabled={isDownloading}
            data-testid={ReportPageObjectModel.ReportDownloadButton}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isDownloading ? <CircularProgress size={18} /> : <FileDownloadOutlined fontSize='small' />}
          </IconButton>
        </span>
      </Tooltip>
      <RoundedPopover
        open={isDownloadOpen}
        anchorEl={downloadAnchorElement}
        onClose={closeDownload}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          <StyledListHeader>
            <BodyText>Download Report</BodyText>
          </StyledListHeader>
          <Divider />
          {formats.map(format => (
            <StyledListItem
              key={format}
              onClick={() => void handleDownload(format)}
              sx={{ cursor: 'pointer' }}
              data-testid={`${ReportPageObjectModel.ReportDownloadButton}-${format}-option`}
            >
              <ListItemIcon>
                <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <BodyText>{formatLabels[format]}</BodyText>
            </StyledListItem>
          ))}
        </StyledList>
      </RoundedPopover>
    </>
  );
};
